import React, { useState, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { RadioButton } from "primereact/radiobutton";
import { calcolaCodiceFiscale } from "../../utilities/common.utilities";
import { Button } from "primereact/button";

type CalcData = {
  sesso: "M" | "F";
  comune: string;
  provincia: string;
  dataNascita: Date | null;
};

type Props = {
  onCodiceFiscaleCalcolato?: (cf: string) => void;
  initialNome: string;
  initialCognome: string;
  calcData: CalcData;
  onCalcDataChange: React.Dispatch<React.SetStateAction<CalcData>>;
};

export default function CodiceFiscaleCalculator({
  onCodiceFiscaleCalcolato,
  initialNome,
  initialCognome,
  calcData,
  onCalcDataChange,
}: Props) {
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [codiceFiscale, setCodiceFiscale] = useState("");
  const [hasTryComune, setHasTryComune] = useState(false);
  const [hasTryProvincia, setHasTryProvincia] = useState(false);

  const calendarRef = useRef<any>(null);

  const { sesso, comune, provincia, dataNascita } = calcData;

  const closeCalendar = () => {
    calendarRef.current?.hide?.();
    calendarRef.current?.hideOverlay?.();
  };

  useEffect(() => {
    setNome(initialNome || "");
    setCognome(initialCognome || "");
  }, [initialNome, initialCognome]);

  useEffect(() => {
    setHasTryComune(comune.length >= 2);
    setHasTryProvincia(provincia.length === 2);
  }, [comune, provincia]);

  useEffect(() => {
    if (nome && cognome && sesso && dataNascita && comune && provincia) {
      const giorno = dataNascita.getDate();
      const mese = dataNascita.getMonth() + 1;
      const anno = dataNascita.getFullYear();

      const cf = calcolaCodiceFiscale(
        nome,
        cognome,
        giorno,
        mese,
        anno,
        sesso,
        comune,
        provincia
      );

      setCodiceFiscale(cf);
    } else {
      setCodiceFiscale("");
    }
  }, [nome, cognome, sesso, dataNascita, comune, provincia]);

  return (
    <div className="p-fluid mx-3">
      <div className="field">
        <label>Nome</label>
        <InputText value={nome} onChange={(e) => setNome(e.target.value)} />
      </div>

      <div className="field">
        <label>Cognome</label>
        <InputText
          value={cognome}
          onChange={(e) => setCognome(e.target.value)}
        />
      </div>

      <div className="field">
        <label>Data di nascita</label>
        <Calendar
          ref={calendarRef}
          className="cf-calendar"
          value={dataNascita}
          onChange={(e) =>
            onCalcDataChange((prev) => ({
              ...prev,
              dataNascita: e.value as Date | null,
            }))
          }
          showIcon
          icon="pi pi-calendar"
          dateFormat="dd/mm/yy"
          autoFocus={false}
          placeholder="DD/MM/YYYY"
          footerTemplate={() => (
            <div className="flex justify-content-end p-2">
              <Button
                type="button"
                label="Chiudi"
                icon="pi pi-times"
                className="p-button-text p-button-sm cf-calendar-close-btn"
                onClick={closeCalendar}
              />
            </div>
          )}
        />

      </div>

      <div className="field">
        <label>Sesso</label>
        <div className="flex gap-3">
          <div className="flex align-items-center">
            <RadioButton
              className="cf-radio-secondary"
              inputId="m"
              value="M"
              onChange={(e) =>
                onCalcDataChange((prev) => ({
                  ...prev,
                  sesso: e.value,
                }))
              }
              checked={sesso === "M"}
            />
            <label htmlFor="m" className="ml-2">
              Maschio
            </label>
          </div>

          <div className="flex align-items-center">
            <RadioButton
              className="cf-radio-secondary"
              inputId="f"
              value="F"
              onChange={(e) =>
                onCalcDataChange((prev) => ({
                  ...prev,
                  sesso: e.value,
                }))
              }
              checked={sesso === "F"}
            />
            <label htmlFor="f" className="ml-2">
              Femmina
            </label>
          </div>
        </div>
      </div>

      <div className="field">
        <label>Comune di nascita</label>
        <InputText
          value={comune}
          onChange={(e) => {
            const val = e.target.value;
            onCalcDataChange((prev) => ({
              ...prev,
              comune: val,
            }));
          }}
        />
      </div>

      <div className="field">
        <label>Provincia</label>
        <InputText
          value={provincia}
          onChange={(e) => {
            const val = e.target.value.toUpperCase();
            onCalcDataChange((prev) => ({
              ...prev,
              provincia: val,
            }));
          }}
          maxLength={2}
        />
      </div>

      {nome && cognome && sesso && dataNascita && comune && provincia && (
        <div className="field mt-3">
          <label>Codice Fiscale</label>
          <InputText value={codiceFiscale} readOnly />
        </div>
      )}

      <div className="flex justify-content-end mt-3">
        {codiceFiscale ? (
          <Button
            label="Aggiungi codice fiscale"
            icon="pi pi-check"
            onClick={() => onCodiceFiscaleCalcolato?.(codiceFiscale)}
            className="p-button-sm button-add-cf"
          />
        ) : hasTryComune && hasTryProvincia ? (
          <small className="p-error">Verifica comune/provincia</small>
        ) : null}
      </div>
    </div>
  );
}
